import React, { useEffect, useRef, useState } from 'react';
import type { UseMonitoringPage } from '../../useMonitoringPage';
import classes from '../shared/filter-bar.module.scss';

type Filters = UseMonitoringPage['movementFilters'];
type FilterKey = keyof Filters;

const TEXT_FIELDS: { key: FilterKey; label: string }[] = [
  { key: 'sessionName', label: 'הפעלה' },
  { key: 'performedByName', label: 'מלצר' },
  { key: 'sourceTableName', label: 'שולחן מקור' },
  { key: 'targetTableName', label: 'שולחן יעד' },
];

const KIND_OPTIONS = [
  { value: 'full_transfer', label: 'העברה מלאה' },
  { value: 'partial_transfer', label: 'העברה חלקית' },
  { value: 'merge_transfer', label: 'העברה במיזוג' },
];

type Props = Pick<UseMonitoringPage, 'movementFilters' | 'setMovementFilters' | 'clearMovementFilters'>;

export const TransferLogsFilters: React.FC<Props> = ({ movementFilters, setMovementFilters, clearMovementFilters }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [scopedKeys, setScopedKeys] = useState<FilterKey[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // No scope = global OR search; scoped = per-field AND search
  useEffect(() => {
    if (scopedKeys.length === 0) {
      const update: Partial<Filters> = { search: searchText };
      TEXT_FIELDS.forEach(({ key }) => { update[key] = ''; });
      setMovementFilters((prev) => ({ ...prev, ...update }));
    } else {
      const update: Partial<Filters> = { search: '' };
      TEXT_FIELDS.forEach(({ key }) => {
        update[key] = scopedKeys.includes(key) ? searchText : '';
      });
      setMovementFilters((prev) => ({ ...prev, ...update }));
    }
  }, [searchText, scopedKeys, setMovementFilters]);

  const toggleScope = (key: FilterKey) => {
    setScopedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleKindToggle = (value: string) => {
    setMovementFilters((f) => ({ ...f, transferKind: f.transferKind === value ? '' : value }));
  };

  const clearAll = () => {
    setScopedKeys([]);
    setSearchText('');
    clearMovementFilters();
  };

  const hasDateFrom = !!movementFilters.dateFrom;
  const hasDateTo = !!movementFilters.dateTo;
  const hasActive = scopedKeys.length > 0 || !!movementFilters.transferKind || hasDateFrom || hasDateTo;

  return (
    <div className={classes.root}>
      <div className={classes.searchBar}>
        {/* chips */}
        {scopedKeys.map((key) => (
          <span key={key} className={classes.chip}>
            {TEXT_FIELDS.find(f => f.key === key)?.label}
            <button className={classes.chipRemove} type="button" data-testid={`monitoring-transfers-chip-remove-scope-${key}`}
              onClick={(e) => { e.stopPropagation(); toggleScope(key); }}>{'×'}</button>
          </span>
        ))}
        {movementFilters.transferKind && (
          <span className={classes.chip}>
            {KIND_OPTIONS.find(k => k.value === movementFilters.transferKind)?.label}
            <button className={classes.chipRemove} type="button" data-testid="monitoring-transfers-chip-remove-kind"
              onClick={(e) => { e.stopPropagation(); handleKindToggle(movementFilters.transferKind); }}>{'×'}</button>
          </span>
        )}
        {hasDateFrom && (
          <span className={classes.chip}>
            {'מ־' + movementFilters.dateFrom}
            <button className={classes.chipRemove} type="button" data-testid="monitoring-transfers-chip-remove-date-from"
              onClick={(e) => { e.stopPropagation(); setMovementFilters((f) => ({ ...f, dateFrom: '' })); }}>{'×'}</button>
          </span>
        )}
        {hasDateTo && (
          <span className={classes.chip}>
            {'עד ' + movementFilters.dateTo}
            <button className={classes.chipRemove} type="button" data-testid="monitoring-transfers-chip-remove-date-to"
              onClick={(e) => { e.stopPropagation(); setMovementFilters((f) => ({ ...f, dateTo: '' })); }}>{'×'}</button>
          </span>
        )}
        <input
          ref={inputRef}
          className={classes.input}
          placeholder={!hasActive ? 'חיפוש...' : ''}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          data-testid="monitoring-transfers-search"
        />
        {hasActive && (
          <button className={classes.clearBtn} type="button" onClick={clearAll} data-testid="monitoring-transfers-clear">{'×'}</button>
        )}
        <button
          className={`${classes.filterBtn} ${hasActive ? classes.filterBtnActive : ''}`}
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          aria-label="סינון"
          data-testid="monitoring-transfers-filter-toggle"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1 2.5A.5.5 0 0 1 1.5 2h13a.5.5 0 0 1 .354.854L10 8.207V13.5a.5.5 0 0 1-.276.447l-3 1.5A.5.5 0 0 1 6 15V8.207L1.146 2.854A.5.5 0 0 1 1 2.5z"/>
          </svg>
        </button>
      </div>

      {dropdownOpen && (
        <>
          <div className={classes.dropdown}>
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'שדות חיפוש'}</div>
              {TEXT_FIELDS.map((f) => (
                <div
                  key={f.key}
                  className={`${classes.dropdownItem} ${scopedKeys.includes(f.key) ? classes.active : ''}`}
                  onClick={() => toggleScope(f.key)}
                  data-testid={`monitoring-transfers-field-${f.key}`}
                >
                  <span className={classes.checkmark}>{scopedKeys.includes(f.key) ? '✓' : ''}</span>
                  {f.label}
                </div>
              ))}
            </div>
            <div className={classes.dropdownDivider} />
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'תאריך'}</div>
              <div className={classes.dateRow}>
                <label className={classes.dateLabel}>{'מ־'}</label>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={movementFilters.dateFrom}
                  onChange={(e) => setMovementFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  data-testid="monitoring-transfers-date-from"
                />
              </div>
              <div className={classes.dateRow}>
                <label className={classes.dateLabel}>{'עד'}</label>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={movementFilters.dateTo}
                  onChange={(e) => setMovementFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  data-testid="monitoring-transfers-date-to"
                />
              </div>
            </div>
            <div className={classes.dropdownDivider} />
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'סוג העברה'}</div>
              {KIND_OPTIONS.map((k) => (
                <div
                  key={k.value}
                  className={`${classes.dropdownItem} ${movementFilters.transferKind === k.value ? classes.active : ''}`}
                  onClick={() => handleKindToggle(k.value)}
                  data-testid={`monitoring-transfers-kind-${k.value}`}
                >
                  <span className={classes.checkmark}>{movementFilters.transferKind === k.value ? '✓' : ''}</span>
                  {k.label}
                </div>
              ))}
            </div>
          </div>
          <div className={classes.backdrop} onClick={() => setDropdownOpen(false)} />
        </>
      )}
    </div>
  );
};
